// ADR-0006-debt: `buildLeadsFilterSummary` operates on `ILeadsSearchFiltersDto`, a `domain/search/`
// type per ADR-0006 still housed in `leads-search-filters` pending Phase 5 of the signals migration.
// Until `domain/search/` exists this is a feature-module → feature-module dependency, accepted as
// documented technical debt — it becomes a one-line import-path change on migration.
import type { IndustryTaxonomyIndex } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import type { ICompanyHeadquarterFilterDto } from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import {
    buildLeadsFilterSummary,
    COUNTRY_LIST_KEYS,
    type FilterSummaryLine,
} from "app/pages/sales-intelligence/modules/leads-search-filters/utils/buildLeadsFilterChips";
import { leadsFilterDtoToPanelValue } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/leadsFilterDtoAdapters";
import { isLeadsPanelFilterAtProductDefault } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/leadsPanelFilterProductDefault";
import { siCountryService } from "app/pages/sales-intelligence/services/siCountryService";
import { buildLocationChips } from "app/pages/sales-intelligence/utils/locationChips";
import type { ICountryObject } from "app/services/CountryService";
import type { IRegionObject } from "app/services/RegionService";

import type { TeamFilters } from "../../types/teams.types";

const isCompanyHeadquarterValue = (value: unknown): value is ICompanyHeadquarterFilterDto =>
    value != null && typeof value === "object" && !Array.isArray(value);

const toCollapsedLocationLabels = (
    value: Parameters<typeof buildLocationChips>[0]["value"],
    regions: readonly IRegionObject[],
    countries: readonly ICountryObject[],
): string[] => buildLocationChips({ value, regions, countries }).map((chip) => chip.text);

const collapseLocationSummaryValue = (key: string, value: unknown): string | null => {
    if (COUNTRY_LIST_KEYS.has(key) && Array.isArray(value)) {
        const labels = toCollapsedLocationLabels(
            { codes: value as number[] },
            siCountryService.allRegions,
            siCountryService.trafficCountriesWithoutWorldwide,
        );

        return labels.length > 0 ? labels.join(", ") : null;
    }

    if (key === "companyHeadquarter" && isCompanyHeadquarterValue(value)) {
        const labels = [
            ...toCollapsedLocationLabels(value, siCountryService.allRegions, siCountryService.allCountries),
            ...(value.zip ?? []),
        ];

        return labels.length > 0 ? labels.join(", ") : null;
    }

    return null;
};

/**
 * One line per configured team filter, listing every selected value — the tooltip scrolls instead of
 * truncating, hence no per-line value cap and the default separator. Pass `taxonomy` to name
 * industry-taxonomy nodes; without it they degrade to a count.
 */
export const buildTeamFiltersSummaryLines = (
    filters: TeamFilters | undefined,
    titleByKey: ReadonlyMap<string, string>,
    taxonomy?: IndustryTaxonomyIndex,
): FilterSummaryLine[] => {
    const panelValue = leadsFilterDtoToPanelValue(filters ?? {}) as Record<string, unknown>;
    const summaryLines = buildLeadsFilterSummary(filters, titleByKey, undefined, undefined, taxonomy);

    return summaryLines
        .filter((line) => !isLeadsPanelFilterAtProductDefault(line.id, panelValue[line.id]))
        .map((line) => {
            const collapsedValue = collapseLocationSummaryValue(line.id, panelValue[line.id]);

            return collapsedValue != null ? { ...line, value: collapsedValue } : line;
        });
};
